/**
 * SuggestedContactsBlock — Salesflare-style auto-discovered contacts for an
 * existing account. Shows people who emailed this account but aren't yet
 * contacts, with one-click accept (creates contact pre-filled from email
 * signature) and dismiss.
 *
 * Compact mode trims down spacing/typography for narrow right-rail use.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Phone as PhoneIcon, Check, X as XIcon, Linkedin, MapPin } from 'lucide-react';
import { API } from '@/App';

const _AV_TONES = [
  'bg-amber-400 text-white',
  'bg-rose-400 text-white',
  'bg-emerald-400 text-white',
  'bg-sky-400 text-white',
  'bg-indigo-400 text-white',
  'bg-purple-400 text-white',
  'bg-orange-400 text-white',
  'bg-teal-400 text-white',
];
function _avatarTone(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h << 5) - h + seed.charCodeAt(i);
  return _AV_TONES[Math.abs(h) % _AV_TONES.length];
}
export function ColoredAvatar({ name, email, size = 32 }) {
  const initial = (name || email || '?').replace(/[^a-zA-Z]/g, '').slice(0, 1).toUpperCase() || '?';
  const px = size + 'px';
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold flex-shrink-0 ${_avatarTone(email || name)}`}
      style={{ width: px, height: px, fontSize: Math.round(size * 0.42) }}
    >
      {initial}
    </span>
  );
}

export default function SuggestedContactsBlock({ companyId, compact = false, onContactsChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyEmail, setBusyEmail] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/companies/${companyId}/suggested-contacts`);
      setItems(r.data?.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const accept = async (s) => {
    setBusyEmail(s.email);
    try {
      const res = await axios.post(`${API}/companies/${companyId}/contacts/from-suggestion`, s);
      if (res.data?.created) toast.success(`${s.name || s.email} added to contacts`);
      else toast.info(`${s.name || s.email} is already a contact`);
      setItems((prev) => prev.filter((x) => x.email !== s.email));
      onContactsChanged && onContactsChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to accept');
    } finally {
      setBusyEmail(null);
    }
  };

  const dismiss = async (s) => {
    setBusyEmail(s.email);
    try {
      await axios.post(`${API}/companies/${companyId}/suggested-contacts/dismiss`, { email: s.email });
      setItems((prev) => prev.filter((x) => x.email !== s.email));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setBusyEmail(null);
    }
  };

  const acceptAll = async () => {
    if (!items.length) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/companies/${companyId}/contacts/from-suggestion/bulk`, { contacts: items });
      const created = res.data?.created || 0;
      const skipped = res.data?.skipped || 0;
      toast.success(`Added ${created} contact${created === 1 ? '' : 's'}${skipped ? ` · ${skipped} skipped` : ''}`);
      setItems([]);
      onContactsChanged && onContactsChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk accept failed');
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={`${compact ? 'mb-3 px-2 py-2 text-[10px]' : 'mb-4 px-4 py-3 text-xs'} border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-900/40 text-slate-400`} data-testid="suggested-contacts-loading">
        Scanning emails for new contacts…
      </div>
    );
  }
  if (!items.length) return null;

  if (compact) {
    return (
      <div className="border border-blue-200 dark:border-blue-900/50 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 overflow-hidden mb-3" data-testid="suggested-contacts-block-compact">
        <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-blue-200/60 dark:border-blue-900/40">
          <div className="flex items-center gap-1.5 min-w-0">
            <Sparkles className="w-3 h-3 text-amber-500 flex-shrink-0" />
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-blue-900 dark:text-blue-200 truncate">
              Suggested ({items.length})
            </h4>
          </div>
          <button
            onClick={acceptAll}
            disabled={bulkBusy}
            className="text-[10px] font-medium text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 disabled:opacity-50 flex-shrink-0"
            data-testid="suggested-accept-all-compact"
          >
            {bulkBusy ? 'Adding…' : 'Accept all'}
          </button>
        </div>
        <div className="divide-y divide-blue-100 dark:divide-blue-900/30 max-h-64 overflow-y-auto">
          {items.map((s) => (
            <SuggestedContactRowCompact
              key={s.email}
              s={s}
              busy={busyEmail === s.email}
              onAccept={() => accept(s)}
              onDismiss={() => dismiss(s)}
            />
          ))}
        </div>
      </div>
    );
  }

  // ─── Full-width (Stakeholders tab) variant ───────────────────────────
  return (
    <div className="mb-6 border border-blue-200 dark:border-blue-900/50 rounded-lg bg-blue-50/40 dark:bg-blue-900/10 overflow-hidden" data-testid="suggested-contacts-block">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-blue-200/60 dark:border-blue-900/40">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-blue-900 dark:text-blue-200">
            Suggested contacts ({items.length})
          </h4>
          <span className="text-[10px] text-blue-700/70 dark:text-blue-300/70">from email signatures</span>
        </div>
        <button
          onClick={acceptAll}
          disabled={bulkBusy}
          className="text-xs font-medium text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 disabled:opacity-50"
          data-testid="suggested-accept-all"
        >
          {bulkBusy ? 'Adding…' : 'Accept all'}
        </button>
      </div>
      <div className="divide-y divide-blue-100 dark:divide-blue-900/30">
        {items.map((s) => (
          <SuggestedContactRow
            key={s.email}
            s={s}
            busy={busyEmail === s.email}
            onAccept={() => accept(s)}
            onDismiss={() => dismiss(s)}
          />
        ))}
      </div>
    </div>
  );
}

function SuggestedContactRow({ s, busy, onAccept, onDismiss }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-100/40 dark:hover:bg-blue-900/20 transition-colors" data-testid={`suggested-contact-${s.email}`}>
      <ColoredAvatar name={s.name} email={s.email} size={36} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-900 dark:text-white truncate">{s.name || s.email.split('@')[0]}</span>
          {s.designation && (
            <span className="text-[10px] text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded bg-slate-200/70 dark:bg-slate-700/70 truncate max-w-[200px]">
              {s.designation}
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{s.email}</p>
        <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500 dark:text-slate-400 flex-wrap">
          {s.phone && <span className="inline-flex items-center gap-1"><PhoneIcon className="w-3 h-3" /> {s.phone}</span>}
          {s.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.city}{s.pincode ? ' · ' + s.pincode : ''}</span>}
          {s.linkedin_url && <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}><Linkedin className="w-3 h-3" /> LinkedIn</a>}
          <span>· {s.email_count} email{s.email_count === 1 ? '' : 's'}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={onAccept} disabled={busy} className="w-7 h-7 inline-flex items-center justify-center rounded text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-40" title="Add as contact" data-testid={`suggested-accept-${s.email}`}>
          <Check className="w-4 h-4" />
        </button>
        <button onClick={onDismiss} disabled={busy} className="w-7 h-7 inline-flex items-center justify-center rounded text-rose-600 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40 disabled:opacity-40" title="Dismiss" data-testid={`suggested-dismiss-${s.email}`}>
          <XIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function SuggestedContactRowCompact({ s, busy, onAccept, onDismiss }) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-blue-100/40 dark:hover:bg-blue-900/20 transition-colors" data-testid={`suggested-contact-compact-${s.email}`}>
      <ColoredAvatar name={s.name} email={s.email} size={26} />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium text-slate-900 dark:text-white truncate">{s.name || s.email.split('@')[0]}</p>
        <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{s.designation || s.email}</p>
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button onClick={onAccept} disabled={busy} className="w-6 h-6 inline-flex items-center justify-center rounded text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-40" title="Add as contact" data-testid={`suggested-accept-compact-${s.email}`}>
          <Check className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDismiss} disabled={busy} className="w-6 h-6 inline-flex items-center justify-center rounded text-rose-600 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40 disabled:opacity-40" title="Dismiss" data-testid={`suggested-dismiss-compact-${s.email}`}>
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
